import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowLeft, Save, Send, Trash2 } from 'lucide-react';
import {
  TemplateEditor,
  templateToValues,
  validateTemplate,
  type TemplateEditorValues,
} from '~/components/TemplateEditor/TemplateEditor';
import {
  useEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
  useSendTestEmail,
} from '~/lib/automations-queries';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { currentUser } from '~/lib/pb';
import styles from './_authed.automations.templates_.editor.module.css';

export const Route = createFileRoute('/_authed/automations/templates_/$templateId')({
  component: EditTemplatePage,
});

function EditTemplatePage() {
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: template, isLoading } = useEmailTemplate(templateId);
  const update = useUpdateEmailTemplate();
  const del = useDeleteEmailTemplate();
  const sendTest = useSendTestEmail();

  const [values, setValues] = useState<TemplateEditorValues | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState<string>(() => currentUser()?.email ?? '');
  const [testResult, setTestResult] = useState<string | null>(null);

  // Hidratar `values` cuando el template se carga. Antes esto se hacía
  // dentro del render (setState durante render), lo cual causaba React
  // error #130 en producción con React 19 — element type undefined al
  // re-renderar inmediatamente. Mover a useEffect lo arregla.
  useEffect(() => {
    if (template && values === null) {
      setValues(templateToValues(template));
    }
  }, [template, values]);

  async function handleSave() {
    if (!values) return;
    setError(null);
    const validationError = validateTemplate(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await update.mutateAsync({
        id: templateId,
        patch: { ...values },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  async function handleSendTest() {
    setTestResult(null);
    setError(null);
    if (!testEmail.trim()) {
      setError('Email destino requerido');
      return;
    }
    try {
      const res = await sendTest.mutateAsync({ templateId, to: testEmail.trim() });
      if (res.ok) {
        setTestResult(`✓ Email enviado a ${res.to} (id: ${res.message_id ?? '—'})`);
      } else {
        setError(`Error: ${res.error ?? 'desconocido'}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al enviar test');
    }
  }

  async function handleDelete() {
    if (!template) return;
    const ok = await confirm({
      title: `Eliminar plantilla "${template.name}"`,
      description: (
        <>
          Los flujos que la usen quedarán con error al ejecutar. Esta
          acción no se puede deshacer.
        </>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(templateId);
      void navigate({ to: '/automations/templates' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  }

  if (isLoading || !values) {
    return (
      <div className={styles.page}>
        <p className="muted">Cargando plantilla…</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/automations/templates" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Plantillas
        </Link>
        <div className={styles.titleRow}>
          <h2 className="h4" style={{ margin: 0 }}>{template?.name || 'Editar plantilla'}</h2>
          <div className={styles.headerActions}>
            <div className={styles.testGroup}>
              <input
                type="email"
                className={styles.testInput}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="tu@email.com"
                aria-label="Email destino para test"
              />
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={handleSendTest}
                disabled={sendTest.isPending}
                title="Enviar email de prueba con SAMPLE_CONTEXT"
              >
                <Send size={14} aria-hidden />
                {sendTest.isPending ? 'Enviando…' : 'Enviar test'}
              </button>
            </div>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleDelete}
              disabled={del.isPending}
              title="Eliminar plantilla"
            >
              <Trash2 size={14} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={update.isPending}
            >
              <Save size={14} aria-hidden />
              {update.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
        {error && <p className={styles.errorBanner}>{error}</p>}
        {testResult && <p className={styles.successBanner}>{testResult}</p>}
      </header>

      <TemplateEditor
        mode="edit"
        initial={values}
        saving={update.isPending}
        onChange={setValues}
        templateId={templateId}
      />
    </div>
  );
}
